import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Check, ChevronDown, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useLanguage, LANGS, tr } from "../i18n";
import { useAuth } from "../auth/AuthContext";
export const lp = (lang, path) => `/${lang}${path === "/" ? "" : path}`;
const LANG_NAMES = {
  en: "English",
  nl: "Nederlands",
  fr: "Français",
  de: "Deutsch"
};
export default function Navbar() {
  const {
    lang,
    t
  } = useLanguage();
  const {
    user,
    logout
  } = useAuth();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const close = e => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);
  const links = [{
    to: "/",
    label: t.nav.home
  }, {
    to: "/about",
    label: t.nav.about
  }, {
    to: "/standards",
    label: t.nav.standards
  }, {
    to: "/services",
    label: t.nav.services
  }, {
    to: "/certification",
    label: t.nav.certification
  }, {
    to: "/eri",
    label: t.nav.eri
  }, {
    to: "/insights",
    label: t.nav.insights
  }, {
    to: "/contact",
    label: t.nav.contact
  }];
  const switchLang = l => {
    const rest = location.pathname.replace(/^\/(en|nl|fr|de)/, "") || "/";
    navigate(lp(l, rest) + location.search);
    setOpen(false);
  };
  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate(lp(lang, "/"));
  };
  return <header className="sticky top-0 z-50 border-b border-esaia-line/80 bg-white/90 backdrop-blur-xl">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to={lp(lang, "/")} aria-label={tr("ESAIA home")} onClick={() => setOpen(false)}>
          <Logo />
        </Link>

        <nav className="hidden xl:flex items-center gap-6" aria-label={tr("Primary")}>
          {links.filter(l => l.to !== "/").map(l => <NavLink key={l.to} to={lp(lang, l.to)} data-testid={`nav-link-${l.to.slice(1)}`} className={({
          isActive
        }) => `whitespace-nowrap text-[13px] font-medium tracking-wide transition-colors ${isActive ? "text-esaia-blue" : "text-esaia-muted hover:text-esaia-ink"}`}>
              {l.label}
            </NavLink>)}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <Link to={lp(lang, "/eri")} data-testid="navbar-cta-eri" className="whitespace-nowrap border border-esaia-blue/40 px-4 py-2 text-[13px] font-semibold text-esaia-blue transition-colors hover:bg-esaia-blue hover:text-white">
            {t.nav.ctaEri}
          </Link>
          <span className="mx-1 h-5 w-px bg-esaia-line" aria-hidden="true" />
          {user ? <>
              <Link to={lp(lang, user.role === "admin" ? "/admin" : "/portal")} data-testid="navbar-portal-link" className="whitespace-nowrap text-[13px] font-semibold text-esaia-blue hover:underline">
                {user.role === "admin" ? tr("Admin Portal") : tr("My Portal")}
              </Link>
              <button onClick={handleLogout} data-testid="navbar-logout-button" className="whitespace-nowrap text-[13px] font-medium text-esaia-muted transition-colors hover:text-red-600">{tr("Log out")}{" "}</button>
            </> : <Link to={lp(lang, "/login")} data-testid="navbar-login-link" className="whitespace-nowrap text-[13px] font-medium text-esaia-muted transition-colors hover:text-esaia-blue">{tr("Log in")}{" "}</Link>}
          <div className="relative" ref={langRef} data-testid="navbar-language-selector">
            <button onClick={() => setLangOpen(!langOpen)} data-testid="language-dropdown-toggle" aria-haspopup="listbox" aria-expanded={langOpen} className="flex items-center gap-1.5 border border-esaia-line px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-esaia-muted transition-colors hover:border-esaia-blue hover:text-esaia-blue">
              {lang}
              <ChevronDown size={13} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && <div className="absolute right-0 top-full z-50 mt-2 w-44 border border-esaia-line bg-white py-1 shadow-lg" role="listbox" data-testid="language-dropdown-menu">
                {LANGS.map(l => <button key={l} role="option" aria-selected={l === lang} onClick={() => {
              switchLang(l);
              setLangOpen(false);
            }} data-testid={`lang-switch-${l}`} className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-[13px] transition-colors hover:bg-esaia-canvas ${l === lang ? "font-semibold text-esaia-blue" : "text-esaia-ink"}`}>
                    <span>{LANG_NAMES[l]}</span>
                    <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-esaia-subtle">
                      {l}
                      {l === lang && <Check size={13} className="text-esaia-blue" />}
                    </span>
                  </button>)}
              </div>}
          </div>
        </div>

        <button className="xl:hidden text-esaia-ink" onClick={() => setOpen(!open)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} data-testid="mobile-menu-toggle">
          {open ? <Menu className="hidden" /> : null}
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && <div className="xl:hidden border-t border-esaia-line bg-white" data-testid="mobile-menu">
          <nav className="mx-auto flex max-w-7xl flex-col px-4 py-4 sm:px-6" aria-label={tr("Mobile")}>
            {links.map(l => <NavLink key={l.to} to={lp(lang, l.to)} end={l.to === "/"} onClick={() => setOpen(false)} className={({
          isActive
        }) => `border-b border-esaia-line/60 py-3 text-sm font-medium ${isActive ? "text-esaia-blue" : "text-esaia-ink"}`}>
                {l.label}
              </NavLink>)}
            {user ? <>
                <NavLink to={lp(lang, user.role === "admin" ? "/admin" : "/portal")} onClick={() => setOpen(false)} data-testid="mobile-portal-link" className="border-b border-esaia-line/60 py-3 text-sm font-semibold text-esaia-blue">
                  {user.role === "admin" ? tr("Admin Portal") : tr("My Portal")}
                </NavLink>
                <button onClick={handleLogout} data-testid="mobile-logout-button" className="border-b border-esaia-line/60 py-3 text-left text-sm font-medium text-esaia-muted">{tr("Log out")}{" "}</button>
              </> : <NavLink to={lp(lang, "/login")} onClick={() => setOpen(false)} data-testid="mobile-login-link" className="border-b border-esaia-line/60 py-3 text-sm font-medium text-esaia-ink">{tr("Log in")}{" "}</NavLink>}
            <div className="flex items-center gap-2 py-4 font-mono text-xs tracking-widest text-esaia-subtle">
              {LANGS.map(l => <button key={l} onClick={() => switchLang(l)} className={`border px-3 py-1.5 uppercase ${l === lang ? "border-esaia-blue text-esaia-blue" : "border-esaia-line"}`}>
                  {l}
                </button>)}
            </div>
            <div className="flex flex-col gap-3 pb-4">
              <Link to={lp(lang, "/eri")} onClick={() => setOpen(false)} className="border border-esaia-blue/40 px-4 py-3 text-center text-sm font-semibold text-esaia-blue">
                {t.nav.ctaEri}
              </Link>
            </div>
          </nav>
        </div>}
    </header>;
}
